// ステップレベルのステータスID定数（AI判定結果用）
// これらはテストケースレベルのステータスIDとは異なるUUIDを使用
export const STEP_STATUS_IDS = {
  // 確信あり
  AI_OK: "0aa084ea-af4c-5173-bebd-bf241bc30529",
  AI_RESULT_NG: "76f27f6a-6f8e-52bc-85f2-c99edc621338",
  AI_PROCEDURE_NG: "3f74b068-8dac-5ca8-9e7e-b0bff5137455",
  // 要確認
  AI_OK_NEED_REVIEW: "674ff3fe-81dd-5f86-9c72-3280cf3e5838",
  AI_RESULT_NG_NEED_REVIEW: "53bb2e13-d5a0-514b-a4fe-e8c938f49cb9",
  AI_PROCEDURE_NG_NEED_REVIEW: "36208def-29dd-5a23-924a-4467c6f2e656",
} as const;

// ステップステータスIDから表示名へのマッピング
export const STEP_STATUS_DISPLAY_MAP: Record<string, string> = {
  // 確信あり
  [STEP_STATUS_IDS.AI_OK]: "OK",
  [STEP_STATUS_IDS.AI_RESULT_NG]: "結果NG",
  [STEP_STATUS_IDS.AI_PROCEDURE_NG]: "手順NG",
  // 要確認
  [STEP_STATUS_IDS.AI_OK_NEED_REVIEW]: "OK(要確認)",
  [STEP_STATUS_IDS.AI_RESULT_NG_NEED_REVIEW]: "結果NG(要確認)",
  [STEP_STATUS_IDS.AI_PROCEDURE_NG_NEED_REVIEW]: "手順NG(要確認)",
};

// ステップステータスIDから色用の判定へのマッピング（要確認は基本判定に正規化）
export const STEP_STATUS_COLOR_MAP: Record<string, string> = {
  // 確信あり
  [STEP_STATUS_IDS.AI_OK]: "OK",
  [STEP_STATUS_IDS.AI_RESULT_NG]: "結果NG",
  [STEP_STATUS_IDS.AI_PROCEDURE_NG]: "手順NG",
  // 要確認（基本判定に正規化）
  [STEP_STATUS_IDS.AI_OK_NEED_REVIEW]: "OK",
  [STEP_STATUS_IDS.AI_RESULT_NG_NEED_REVIEW]: "結果NG",
  [STEP_STATUS_IDS.AI_PROCEDURE_NG_NEED_REVIEW]: "手順NG",
};

/**
 * ステップのstatusIdから判定文字列を取得（色・比較用）
 * @param statusId ステップのステータスID
 * @returns 判定文字列 ('OK' | '手順NG' | '結果NG' | '未評価')
 */
export const getStepJudgment = (statusId?: string): string => {
  if (statusId && STEP_STATUS_COLOR_MAP[statusId]) {
    return STEP_STATUS_COLOR_MAP[statusId];
  }
  return "未評価";
};

/**
 * ステップのstatusIdから表示用判定文字列を取得
 * @param statusId ステップのステータスID
 * @returns 表示用判定文字列
 */
export const getStepJudgmentDisplay = (statusId?: string): string => {
  if (statusId && STEP_STATUS_DISPLAY_MAP[statusId]) {
    return STEP_STATUS_DISPLAY_MAP[statusId];
  }
  return "未評価";
};

// APIクライアントクラス
export class SSTFApiClient {
  private sstfBaseUrl: string;
  // userToken removed - using Auth0 authentication
  private graphqlBaseUrl: string;
  private useProxy: boolean;
  private proxyUrl: string;

  constructor(
    sstfBaseUrl: string,
    userToken: string, // Kept for backward compatibility but not used
    graphqlBaseUrl?: string,
    useProxy: boolean = true
  ) {
    this.sstfBaseUrl = sstfBaseUrl;
    // userToken removed - using Auth0 authentication
    // GraphQLエンドポイントが指定されていない場合は、デフォルトを使用
    this.graphqlBaseUrl = graphqlBaseUrl || "https://gql.dev.sstf.agest.app";
    this.useProxy = useProxy;

    // プロキシURL設定の改善
    this.proxyUrl = this.getProxyUrl();

    console.log("SSTFApiClient初期化:", {
      sstfBaseUrl: this.sstfBaseUrl,
      graphqlBaseUrl: this.graphqlBaseUrl,
      useProxy: this.useProxy,
      proxyUrl: this.proxyUrl,
      // userToken removed - using Auth0 authentication
    });
  }

  // プロキシURLを適切に設定するメソッド
  private getProxyUrl(): string {
    // 開発環境とプロダクション環境での適切なプロキシURL設定
    if (typeof window !== "undefined") {
      const origin = window.location.origin;
      console.log("現在のオリジン:", origin);

      // ローカル開発環境の場合
      if (
        origin.includes("localhost:3000") ||
        origin.includes("127.0.0.1:3000")
      ) {
        return "http://localhost:3001";
      }

      // nginx経由の場合は同一オリジン
      return origin;
    }

    // フォールバック
    return "http://localhost";
  }

  // プロキシサーバーの接続確認メソッドを追加
  async testProxyConnection(): Promise<{ success: boolean; message: string }> {
    try {
      console.log("プロキシサーバー接続確認開始:", this.proxyUrl);

      const healthUrl = `${this.proxyUrl}/health`;
      const response = await fetch(healthUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: 'include', // Important for cookie-based session
        // タイムアウト設定
        signal: AbortSignal.timeout(10000), // 10秒
      });

      if (!response.ok) {
        throw new Error(
          `プロキシサーバーからの応答エラー: ${response.status} ${response.statusText}`
        );
      }

      const healthData = await response.json();
      console.log("プロキシサーバー接続確認成功:", healthData);

      return {
        success: true,
        message: `プロキシサーバー接続成功 (${healthData.service})`,
      };
    } catch (error) {
      console.error("プロキシサーバー接続確認失敗:", error);

      const errorMessage =
        error instanceof Error ? error.message : "不明なエラー";
      return {
        success: false,
        message: `プロキシサーバー接続失敗: ${errorMessage}`,
      };
    }
  }

  // プロキシ経由のリクエストメソッド
  private async proxyRequest<T>(
    endpoint: string,
    method: string = "GET",
    params?: Record<string, string>,
    data?: any
  ): Promise<T> {
    console.log("プロキシ経由でAPIリクエスト開始:", {
      method,
      endpoint,
      sstfBaseUrl: this.sstfBaseUrl,
      proxyUrl: this.proxyUrl,
      has_params: !!params,
      has_data: !!data,
      params,
      data
    });

    try {
      console.log("Fetchリクエスト開始:", `${this.proxyUrl}/api/proxy`);

      // Prepare headers (session-based auth, no need for Authorization header)
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      console.log("📤 送信ヘッダー:", headers);
      
      // タイムアウト設定（互換性のある方法）
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒
      
      const response = await fetch(`${this.proxyUrl}/api/proxy`, {
        method: "POST",
        headers,
        credentials: 'include', // Important for cookie-based session
        body: JSON.stringify({
          method,
          endpoint,
          params,
          data,
          sstf_base_url: this.sstfBaseUrl,
        }),
        signal: controller.signal,
      })
      .then(res => {
        console.log("Fetchレスポンス受信:", res.status, res.statusText);
        return res;
      })
      .catch(err => {
        console.error("Fetchエラー:", err);
        throw err;
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

      console.log("プロキシレスポンス受信:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("プロキシエラーレスポンス:", {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });

        // Handle 401 Unauthorized errors (authentication error)
        if (response.status === 401) {
          console.warn("🔒 401 Unauthorized error detected. Session may be expired.");
          throw new Error("認証エラーが発生しました。再度ログインしてください。");
        }

        // エラーメッセージを詳細化
        let errorMessage = `Proxy request failed: ${response.status} ${response.statusText}`;

        if (response.status === 0 || response.status >= 500) {
          errorMessage +=
            "\n\nプロキシサーバーが応答しません。以下を確認してください:";
          errorMessage +=
            "\n1. docker-compose up -d でサービスが起動しているか";
          errorMessage +=
            "\n2. プロキシサーバー（proxy-server）が正常に動作しているか";
          errorMessage += "\n3. nginx設定でプロキシが正しく設定されているか";
        } else if (response.status === 404) {
          errorMessage +=
            "\n\nプロキシエンドポイントが見つかりません。nginx設定を確認してください。";
        }

        if (errorText) {
          errorMessage += `\n\nサーバーエラー詳細: ${errorText}`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("プロキシレスポンス解析完了:", {
        endpoint,
        method,
        has_data: !!result.data,
        metadata: result.metadata,
        data: result.data
      });

      return result.data;
    } catch (error: any) {
      console.error("プロキシリクエスト失敗:", {
        endpoint,
        method,
        proxyUrl: this.proxyUrl,
        error,
        errorMessage: error instanceof Error ? error.message : "不明なエラー",
        errorName: error?.name,
        errorStack: error?.stack,
      });

      // タイムアウトエラーの場合
      if (error.name === 'AbortError') {
        throw new Error(
          `リクエストがタイムアウトしました（30秒）。\nエンドポイント: ${endpoint}`
        );
      }

      // ネットワークエラーの場合は詳細なメッセージを追加
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error(
          `プロキシサーバーに接続できません (${this.proxyUrl})。\n\n以下を確認してください:\n1. docker-compose up -d でサービスが起動しているか\n2. プロキシサーバーが正常に動作しているか\n3. ネットワーク接続に問題がないか\n\n元のエラー: ${error.message}`
        );
      }

      throw error;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // APIのbaseURLが/api/v2を含んでいるかチェック
    const baseUrl = this.sstfBaseUrl.endsWith("/api/v2")
      ? this.sstfBaseUrl
      : `${this.sstfBaseUrl}/api/v2`;
    const url = `${baseUrl}${endpoint}`;

    console.log("HTTP Request開始:", {
      method: options.method || "GET",
      baseUrl: this.sstfBaseUrl,
      finalUrl: url,
      endpoint,
      headers: {
        "Content-Type": "application/json",
        // userToken removed - using Auth0 authentication
      },
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          // userToken removed - using Auth0 authentication
          ...options.headers,
        },
      });

      console.log("HTTP Response受信:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("HTTP Error Response:", {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });
        throw new Error(
          `API request failed: ${response.status} ${response.statusText}\n${errorText}`
        );
      }

      const result = await response.json();
      console.log("HTTP Response JSON解析完了:", result);
      return result;
    } catch (error) {
      console.error("HTTP Request失敗:", {
        url,
        error,
        errorMessage: error instanceof Error ? error.message : "不明なエラー",
      });
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    if (this.useProxy) {
      return this.proxyRequest<T>(endpoint, "GET", params);
    } else {
      const searchParams = params ? new URLSearchParams(params).toString() : "";
      const url = searchParams ? `${endpoint}?${searchParams}` : endpoint;
      return this.request<T>(url);
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    if (this.useProxy) {
      return this.proxyRequest<T>(endpoint, "POST", undefined, data);
    } else {
      return this.request<T>(endpoint, {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      });
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    console.log('PUT request:', {
      endpoint,
      data,
      useProxy: this.useProxy,
      sstfBaseUrl: this.sstfBaseUrl
    });
    
    if (this.useProxy) {
      return this.proxyRequest<T>(endpoint, "PUT", undefined, data);
    } else {
      return this.request<T>(endpoint, {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      });
    }
  }

  // GraphQL専用のリクエストメソッド
  private async graphqlRequest<T>(
    query: string,
    variables?: any,
    operationName?: string
  ): Promise<T> {
    if (this.useProxy) {
      console.log("GraphQL Request開始（プロキシ経由）:", {
        graphqlBaseUrl: this.graphqlBaseUrl,
        operationName,
        variables,
      });

      try {
        // Prepare headers (session-based auth, no need for Authorization header)
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        const response = await fetch(`${this.proxyUrl}/api/proxy`, {
          method: "POST",
          headers,
          credentials: 'include', // Important for cookie-based session
          body: JSON.stringify({
            method: "POST",
            endpoint: "/graphql",
            params: undefined,
            data: {
              query,
              variables,
              operationName,
            },
            sstf_base_url: this.graphqlBaseUrl,
            // user_token removed - using Auth0 authentication
          }),
        });

        console.log("GraphQL Response受信（プロキシ経由）:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("GraphQL Error Response（プロキシ経由）:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(
            `GraphQL proxy request failed: ${response.status} ${response.statusText}\n${errorText}`
          );
        }

        const proxyResult = await response.json();
        const result = proxyResult.data;
        console.log("GraphQL Response JSON解析完了（プロキシ経由）:", result);

        // GraphQLエラーをチェック
        if (result.errors) {
          console.error("GraphQL Errors:", result.errors);
          throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }

        return result;
      } catch (error) {
        console.error("GraphQL Request失敗（プロキシ経由）:", {
          error,
          errorMessage: error instanceof Error ? error.message : "不明なエラー",
        });
        throw error;
      }
    } else {
      // 直接GraphQLエンドポイントにアクセス（従来の方式）
      const url = `${this.graphqlBaseUrl}/graphql`;

      console.log("GraphQL Request開始:", {
        url,
        operationName,
        variables,
      });

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // userToken removed - using Auth0 authentication
          },
          body: JSON.stringify({
            query,
            variables,
            operationName,
          }),
        });

        console.log("GraphQL Response受信:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          url: response.url,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("GraphQL Error Response:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(
            `GraphQL request failed: ${response.status} ${response.statusText}\n${errorText}`
          );
        }

        const result = await response.json();
        console.log("GraphQL Response JSON解析完了:", result);

        // GraphQLエラーをチェック
        if (result.errors) {
          console.error("GraphQL Errors:", result.errors);
          throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }

        return result;
      } catch (error) {
        console.error("GraphQL Request失敗:", {
          url,
          error,
          errorMessage: error instanceof Error ? error.message : "不明なエラー",
        });
        throw error;
      }
    }
  }

  // ワークスペース一覧を取得
  async getWorkspaces(onlyBelonging: boolean = false): Promise<Workspace[]> {
    const params = {
      only_belonging: onlyBelonging ? "true" : "false",
    };
    return this.get<Workspace[]>("/workspaces", params);
  }

  // プロジェクト一覧を取得
  async getProjects(
    workspaceId: string,
    onlyBelonging: boolean = false
  ): Promise<Project[]> {
    const params = {
      workspace_id: workspaceId,
      only_belonging: onlyBelonging ? "true" : "false",
    };
    return this.get<Project[]>("/projects", params);
  }

  // テストラン取得
  async getTestRuns(
    workspaceId: string,
    projectId: string
  ): Promise<TestRun[]> {
    try {
      console.log("テストランを取得中...", { workspaceId, projectId });

      let allTestRuns: TestRun[] = [];
      let currentPage = 0;

      while (true) {
        const request: FindTestRunsRequest = {
          page: currentPage,
          size: 100, // 適切なサイズに調整
          workspace_id: workspaceId,
          project_id: projectId,
        };

        console.log(`ページ ${currentPage} のテストランを取得中...`);

        const response = await this.post<FindTestRunsResponse>(
          "/test_runs/find",
          request
        );

        if (
          !response.test_run_with_summaries ||
          response.test_run_with_summaries.length === 0
        ) {
          console.log(`ページ ${currentPage} のテストランはありませんでした`);
          break;
        }

        // test_run_with_summariesからTestRunオブジェクトを抽出
        const testRuns = response.test_run_with_summaries.map((summary) => ({
          test_run_id: summary.test_run.test_run_id,
          name: summary.test_run.name,
          workspace_id: summary.test_run.workspace_id,
          project_id: summary.test_run.project_id,
        }));

        allTestRuns = allTestRuns.concat(testRuns);

        // 次のページがあるかチェック
        if (!response.pagination?.has_next) {
          break;
        }

        currentPage++;
      }

      console.log(`合計 ${allTestRuns.length} 件のテストランを取得しました`);

      // 名前でソート
      allTestRuns.sort((a, b) => a.name.localeCompare(b.name));

      return allTestRuns;
    } catch (error) {
      console.error("テストラン取得エラー:", error);
      throw new Error(
        `テストランの取得に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // テンプレート取得
  async getTemplate(
    templateType: string,
    workspaceId: string,
    projectId: string
  ): Promise<Template> {
    console.log('getTemplate呼び出し:', {
      templateType,
      workspaceId,
      projectId,
      workspaceId_type: typeof workspaceId,
      projectId_type: typeof projectId
    });
    
    const params = {
      workspace_id: workspaceId,
      project_id: projectId,
    };
    
    console.log('テンプレート取得パラメータ:', params);
    return this.get<Template>(`/templates/${templateType}`, params);
  }

  // テスト結果検索（単一ページのみ取得）
  async findTestResults(
    request: FindTestResultsRequest
  ): Promise<FindTestResultsResponse> {
    // 呼び出し元が指定したページをそのまま使用
    console.log(`ページ ${request.page || 1} のデータを取得中...`);

    const response = await this.post<FindTestResultsResponse>(
      "/test_results/find",
      request
    );

    console.log(`ページ ${request.page || 1} から ${response.script_test_result_details?.length || 0} 件のテスト結果を取得しました`);

    return response;
  }

  // ファイルメタデータ取得
  async getFilesMetadata(
    workspaceId: string,
    projectId: string,
    fileIds: string[]
  ): Promise<FileMetadata[]> {
    const requestData = {
      workspace_id: workspaceId,
      project_id: projectId,
      file_ids: fileIds,
    };

    console.log("ファイルメタデータを取得中...", {
      workspaceId,
      projectId,
      fileIds,
    });

    const response = await this.post<FilesMetadataResponse>(
      "/files/find/meta",
      requestData
    );

    console.log("ファイルメタデータを取得しました:", response);
    // files_info配列を返す
    return response.files_info;
  }

  // ファイルダウンロード用一時URL取得（テキストレスポンス用）
  async getFileDownloadUrl(
    workspaceId: string,
    projectId: string,
    fileId: string
  ): Promise<string> {
    const params = {
      workspace_id: workspaceId,
      project_id: projectId,
    };

    console.log("ファイルダウンロードURLを取得中...", {
      workspaceId,
      projectId,
      fileId,
      params,
      useProxy: this.useProxy,
    });

    if (this.useProxy) {
      try {
        console.log("プロキシ経由でファイルダウンロードURL取得中...");

        // Prepare headers (session-based auth, no need for Authorization header)
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        const response = await fetch(`${this.proxyUrl}/api/proxy`, {
          method: "POST",
          headers,
          credentials: 'include', // Important for cookie-based session
          body: JSON.stringify({
            method: "GET",
            endpoint: `/files/${fileId}`,
            params,
            data: undefined,
            sstf_base_url: this.sstfBaseUrl,
            // user_token removed - using Auth0 authentication
          }),
        });

        console.log("プロキシレスポンス受信:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("プロキシエラーレスポンス:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(
            `Proxy request failed: ${response.status} ${response.statusText}\n${errorText}`
          );
        }

        const result = await response.json();
        const downloadUrl = result.data;

        console.log("ファイルダウンロードURLを取得しました（プロキシ経由）:", {
          downloadUrl:
            downloadUrl.substring(0, 100) +
            (downloadUrl.length > 100 ? "..." : ""),
          fullLength: downloadUrl.length,
        });

        if (
          !downloadUrl ||
          typeof downloadUrl !== "string" ||
          downloadUrl.trim() === ""
        ) {
          throw new Error(
            `有効なダウンロードURLが取得できませんでした: ${downloadUrl}`
          );
        }

        // URLの妥当性をチェック
        const trimmedUrl = downloadUrl.trim();
        if (
          !trimmedUrl.startsWith("http://") &&
          !trimmedUrl.startsWith("https://")
        ) {
          throw new Error(`無効なURL形式です: ${trimmedUrl}`);
        }

        return trimmedUrl;
      } catch (error) {
        console.error("ファイルダウンロードURL取得エラー（プロキシ経由）:", {
          error,
          workspaceId,
          projectId,
          fileId,
          errorMessage: error instanceof Error ? error.message : "不明なエラー",
        });
        throw error;
      }
    } else {
      // 従来の直接アクセス方式
      try {
        // このAPIは文字列を直接返すため、専用の処理が必要
        const searchParams = new URLSearchParams(params).toString();
        const endpoint = `/files/${fileId}?${searchParams}`;

        // APIのbaseURLが/api/v2を含んでいるかチェック
        const baseUrl = this.sstfBaseUrl.endsWith("/api/v2")
          ? this.sstfBaseUrl
          : `${this.sstfBaseUrl}/api/v2`;
        const url = `${baseUrl}${endpoint}`;

        console.log("APIリクエスト実行中:", {
          method: "GET",
          url,
          endpoint,
        });

        const response = await fetch(url, {
          method: "GET",
          headers: {
            // Authorization removed - using Auth0 authentication
          },
        });

        console.log("HTTP Response受信:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          url: response.url,
          contentType: response.headers.get("content-type"),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("HTTP Error Response:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          throw new Error(
            `API request failed: ${response.status} ${response.statusText}\n${errorText}`
          );
        }

        // テキストレスポンスとして取得
        const downloadUrl = await response.text();

        console.log("ファイルダウンロードURLを取得しました:", {
          downloadUrl:
            downloadUrl.substring(0, 100) +
            (downloadUrl.length > 100 ? "..." : ""),
          fullLength: downloadUrl.length,
        });

        if (
          !downloadUrl ||
          typeof downloadUrl !== "string" ||
          downloadUrl.trim() === ""
        ) {
          throw new Error(
            `有効なダウンロードURLが取得できませんでした: ${downloadUrl}`
          );
        }

        // URLの妥当性をチェック
        const trimmedUrl = downloadUrl.trim();
        if (
          !trimmedUrl.startsWith("http://") &&
          !trimmedUrl.startsWith("https://")
        ) {
          throw new Error(`無効なURL形式です: ${trimmedUrl}`);
        }

        return trimmedUrl;
      } catch (error) {
        console.error("ファイルダウンロードURL取得エラー:", {
          error,
          workspaceId,
          projectId,
          fileId,
          errorMessage: error instanceof Error ? error.message : "不明なエラー",
          errorStack: error instanceof Error ? error.stack : undefined,
        });
        throw error;
      }
    }
  }
  async downloadOperationLog(
    workspaceId: string,
    projectId: string,
    fileId: string
  ): Promise<OperationLog> {
    console.log("操作ログをダウンロード中（ローカルプロキシ経由）...", {
      workspaceId,
      projectId,
      fileId,
    });

    try {
      console.log("プロキシサーバー経由でファイル内容を取得中...");

      // Prepare headers (session-based auth, no need for Authorization header)
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // nginx設定に合わせて /files/download パスを使用
      const proxyResponse = await fetch(`${this.proxyUrl}/files/download`, {
        method: "POST",
        headers,
        credentials: 'include', // Important for cookie-based session
        body: JSON.stringify({
          workspace_id: workspaceId,
          project_id: projectId,
          file_id: fileId,
          sstf_base_url: this.sstfBaseUrl,
          // user_token removed - using Auth0 authentication
        }),
      });

      if (!proxyResponse.ok) {
        const errorText = await proxyResponse.text();
        throw new Error(
          `ローカルプロキシエラー: ${proxyResponse.status} ${proxyResponse.statusText}\n${errorText}`
        );
      }

      const result = await proxyResponse.json();
      console.log("操作ログダウンロード完了（プロキシ経由）");
      return JSON.parse(result.content) as OperationLog;
    } catch (error) {
      console.error("操作ログのダウンロードに失敗:", error);
      throw new Error(
        `操作ログのダウンロードに失敗しました。プロキシサーバーとの通信に問題があります。エラー: ${
          error instanceof Error ? error.message : error
        }`
      );
    }
  }

  // エビデンスファイル用のダウンロードURL取得（表示用）
  async getFileDownloadUrlForDisplay(
    workspaceId: string,
    projectId: string,
    fileId: string
  ): Promise<string> {
    // エビデンスファイル（画像・動画）は署名つきURLを直接返す
    // img src や video src で使用するため、fetchは不要
    return await this.getFileDownloadUrl(workspaceId, projectId, fileId);
  }

  /**
   * 複数 file_id をまとめて signed URL に解決する。
   * BFF の `POST /files/bulk-signed-urls` を呼び出す。
   * BFF が未デプロイ等で 404 を返した場合は呼び出し側でフォールバックすること
   * （個別取得にフォールバックする想定）。
   *
   * 戻り値は { fileId -> URL } のマップ。失敗した個別ファイルは含まれない。
   */
  async bulkGetFileDownloadUrls(
    workspaceId: string,
    projectId: string,
    fileIds: string[]
  ): Promise<Record<string, string>> {
    if (fileIds.length === 0) return {};
    if (!this.useProxy) {
      throw new Error("bulkGetFileDownloadUrls はプロキシ経由でのみサポートされます");
    }

    const response = await fetch(`${this.proxyUrl}/api/proxy`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        method: "POST",
        endpoint: "/files/bulk-signed-urls",
        params: undefined,
        data: {
          workspace_id: workspaceId,
          project_id: projectId,
          file_ids: fileIds,
        },
        sstf_base_url: this.sstfBaseUrl,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `bulk signed URL 取得失敗: ${response.status} ${response.statusText}\n${errorText}`
      );
    }

    const result = await response.json();
    const urls = (result?.data?.urls ?? {}) as Record<string, string>;
    return urls;
  }

  // テスト結果更新（REST API版）
  async updateTestResultREST(
    workspaceId: string,
    projectId: string,
    testRunId: string,
    resultId: string,
    scriptTestRunMatrixId: string,
    testResultStatusId: string,
    value?: string,
    actualTimeInSeconds?: number
  ): Promise<any> {
    console.log("GraphQL mutationでテスト結果更新中...", {
      workspaceId,
      projectId,
      testRunId,
      resultId,
      scriptTestRunMatrixId,
      testResultStatusId,
      value,
      actualTimeInSeconds
    });

    // GraphQL mutation定義
    const mutation = `
      mutation updateTestResults($workspaceId: String!, $projectId: String!, $testRunId: String!, $testResultInputs: [UpdateTestResultInput!]!) {
        updateTestResults(updateTestResultsInput: {
          workspaceId: $workspaceId
          projectId: $projectId
          testRunId: $testRunId
          testResultInputs: $testResultInputs
        }) {
          scriptTestRunResult {
            id
          }
        }
      }
    `;

    // UpdateTestResultInput型の構築
    const testResultInput: any = {
      testResultStatusId: testResultStatusId,
      scriptTestRunMatrixId: scriptTestRunMatrixId,
      actualTimeInSeconds: actualTimeInSeconds || 0
    };

    // resultIdがある場合はresultIdを追加
    if (resultId) {
      testResultInput.resultId = resultId;
    }

    // valueがある場合は追加
    if (value) {
      testResultInput.value = value;
    }

    const variables = {
      workspaceId,
      projectId,
      testRunId,
      testResultInputs: [testResultInput]
    };

    console.log("GraphQL variables:", JSON.stringify(variables, null, 2));

    try {
      const response = await this.graphqlRequest(mutation, variables);
      console.log("テスト結果更新成功（GraphQL）:", response);
      return response;
    } catch (error) {
      console.error("テスト結果更新失敗（GraphQL）:", error);
      throw error;
    }
  }

  // テスト結果更新（GraphQL mutation） - 互換性のため残す
  async updateTestResults(
    input: UpdateTestResultsInput
  ): Promise<UpdateTestResultsResponse> {
    const query = `
      mutation updateTestResults($updateTestResultsInput: UpdateTestResultsInput!) {
        updateTestResults(updateTestResultsInput: $updateTestResultsInput) {
          scriptTestRunResult {
            id
            testResultStatusId
            metaInfo {
              modifiedAt
              modifiedBy
            }
          }
        }
      }
    `;

    const variables = {
      updateTestResultsInput: input,
    };

    console.log("テスト結果更新GraphQLクエリ実行中...", {
      input,
      variables,
    });

    // GraphQL専用メソッドを使用
    const response = await this.graphqlRequest<{
      data: { updateTestResults: UpdateTestResultsResponse };
    }>(query, variables, "updateTestResults");

    console.log("テスト結果更新完了:", response);
    return response.data.updateTestResults;
  }

  // テスト結果のvalue更新用メソッド（test_result_status_idの更新もオプションで可能）
  async updateTestResultValue(
    testResultId: number,
    updatedValue: any,
    updatedStatusId?: string
  ): Promise<any> {
    // test_result_status_idの更新がある場合は両方更新、ない場合はvalueのみ更新
    const mutation = updatedStatusId
      ? `
        mutation UpdateTestResultValueAndStatus($id: Int!, $value: JSON!, $statusId: String!) {
          updateScriptTestRunResults(
            id: $id,
            value: $value,
            test_result_status_id: $statusId
          ) {
            id
            test_result_status_id
            value
          }
        }
      `
      : `
        mutation UpdateTestResultValue($id: Int!, $value: JSON!) {
          updateScriptTestRunResults(
            id: $id,
            value: $value
          ) {
            id
            test_result_status_id
            value
          }
        }
      `;

    const variables = updatedStatusId
      ? {
          id: testResultId,
          value: updatedValue,
          statusId: updatedStatusId
        }
      : {
          id: testResultId,
          value: updatedValue
        };

    try {
      const response = await this.graphqlRequest(mutation, variables);
      console.log("テスト結果の更新成功:", response);
      return response;
    } catch (error) {
      console.error("テスト結果の更新失敗:", error);
      throw error;
    }
  }

  // テストターゲット取得
  async getTestTargets(
    workspaceId: string,
    projectId: string
  ): Promise<TestTarget[]> {
    console.log("テストターゲットを取得中...", { workspaceId, projectId });

    const query = `
      query GetTestTargets($getTestTargetsInput: GetTestTargetsInput!) {
        getTestTargets(getTestTargetsInput: $getTestTargetsInput) {
          name
          displayOrder
          workspaceId
          projectId
          testTargetId
          type {
            typeId
            name
          }
          description
          parentTestTargetId
          params
          metaInfo {
            createdAt
            createdBy
            modifiedAt
            modifiedBy
          }
        }
      }
    `;

    const variables = {
      getTestTargetsInput: {
        workspaceId,
        projectId,
      },
    };

    try {
      const response = await this.graphqlRequest<{
        data: { getTestTargets: TestTarget[] };
      }>(query, variables, "GetTestTargets");

      console.log(
        `テストターゲット取得完了: ${response.data.getTestTargets.length}件`
      );
      return response.data.getTestTargets;
    } catch (error) {
      console.error("テストターゲット取得エラー:", error);
      throw new Error(
        `テストターゲットの取得に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  }

  // 要確認ステータスの一括更新（サーバー側で同期処理）
  async updateReviewStatus(
    workspaceId: string,
    projectId: string
  ): Promise<{
    success: boolean;
    total: number;
    updated: number;
    skipped: number;
    errors: number;
    message: string;
    details?: {
      added: Array<{
        testCaseName: string;
        oldStatus: string;
        newStatus: string;
        reason: string;
      }>;
      removed: Array<{
        testCaseName: string;
        oldStatus: string;
        newStatus: string;
        reason: string;
      }>;
      errors: Array<{
        testCaseName: string;
        error: string;
      }>;
    };
  }> {
    console.log('=== 要確認ステータス更新処理開始（同期実行） ===', { workspaceId, projectId });

    // workspaceIdとprojectIdのバリデーション
    if (!workspaceId || !projectId) {
      console.error('必須パラメータが不足:', { workspaceId, projectId });
      return {
        success: false,
        total: 0,
        updated: 0,
        skipped: 0,
        errors: 0,
        message: 'ワークスペースIDとプロジェクトIDが必要です'
      };
    }

    try {
      // Prepare headers (session-based auth, no need for Authorization header)
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // プロキシサーバー経由で同期実行（完了まで待機）
      const proxyUrl = `${this.proxyUrl}/api/schedule/execute-sync`;
      console.log('同期実行リクエストを送信:', proxyUrl);

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers,
        credentials: 'include', // Important for cookie-based session
        body: JSON.stringify({
          workspaceId,
          projectId,
          sstfBaseUrl: this.sstfBaseUrl,
          graphqlBaseUrl: this.graphqlBaseUrl,
          // userToken removed - using Auth0 authentication
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('同期実行エラー:', errorText);
        throw new Error(`ステータス更新に失敗しました: ${response.status}`);
      }

      const result = await response.json();
      console.log('同期実行完了:', result);

      // 実際の結果を返す
      return {
        success: result.success,
        total: result.total || 0,
        updated: result.updated || 0,
        skipped: result.skipped || 0,
        errors: result.errors || 0,
        message: result.message || `更新完了: ${result.updated}件更新`,
        details: result.details || {
          added: [],
          removed: [],
          errors: []
        }
      };
    } catch (error) {
      console.error('同期実行エラー:', error);
      return {
        success: false,
        total: 0,
        updated: 0,
        skipped: 0,
        errors: 1,
        message: `エラー: ${error instanceof Error ? error.message : String(error)}`,
        details: {
          added: [],
          removed: [],
          errors: []
        }
      };
    }
  }
  
  // 要確認ステータスの一括更新（同期版・廃止予定）
  async updateReviewStatusSync(
    workspaceId: string,
    projectId: string
  ): Promise<{
    success: boolean;
    total: number;
    updated: number;
    skipped: number;
    errors: number;
    message: string;
    details?: {
      added: Array<{
        testCaseName: string;
        oldStatus: string;
        newStatus: string;
        reason: string;
      }>;
      removed: Array<{
        testCaseName: string;
        oldStatus: string;
        newStatus: string;
        reason: string;
      }>;
      errors: Array<{
        testCaseName: string;
        error: string;
      }>;
    };
  }> {
    console.log('=== 要確認ステータス更新処理開始（同期版） ===', { workspaceId, projectId });
    
    // workspaceIdとprojectIdのバリデーション
    if (!workspaceId || !projectId) {
      console.error('必須パラメータが不足:', { workspaceId, projectId });
      return {
        success: false,
        total: 0,
        updated: 0,
        skipped: 0,
        errors: 0,
        message: 'ワークスペースIDとプロジェクトIDが必要です'
      };
    }
    
    try {
      // まずプロジェクトのステータスID定義を取得
      console.log('テンプレート取得中...', { workspaceId, projectId });
      const template = await this.getTemplate('test_result', workspaceId, projectId);
      const statusIds = extractProjectStatusIds(template);
      console.log('ステータスID定義:', statusIds);
      console.log('[DEBUG] 詳細なステータスID:', {
        hasStatusIds: !!statusIds,
        hasTestResultStatus: !!statusIds?.testResultStatus,
        AI_OK: statusIds?.testResultStatus?.AI_OK,
        AI_OK_NEED_REVIEW: statusIds?.testResultStatus?.AI_OK_NEED_REVIEW,
        AI_PROCEDURE_NG: statusIds?.testResultStatus?.AI_PROCEDURE_NG,
        AI_PROCEDURE_NG_NEED_REVIEW: statusIds?.testResultStatus?.AI_PROCEDURE_NG_NEED_REVIEW,
        AI_RESULT_NG: statusIds?.testResultStatus?.AI_RESULT_NG,
        AI_RESULT_NG_NEED_REVIEW: statusIds?.testResultStatus?.AI_RESULT_NG_NEED_REVIEW
      });
      
      // 1. テスト結果を取得（REST API使用）
      console.log('テスト結果を取得中...');
      const findRequest: FindTestResultsRequest = {
        page: 1,
        size: 1000, // 一度に多くの結果を取得
        workspace_id: workspaceId,
        project_id: projectId,
        query: {
          and: []  // すべての結果を取得
        }
      };
      
      const testResultsResponse = await this.findTestResults(findRequest);
      console.log(`取得したテスト結果: ${testResultsResponse.script_test_result_details.length}件`);
      
      // テスト結果とテストケース情報を整形（内部処理用の型）
      const testResultsInternal = testResultsResponse.script_test_result_details.map(detail => {
        // テストケース情報を value から抽出
        let testCaseValue: any = {};
        try {
          testCaseValue = detail.test_run_test_case.value ? 
            (typeof detail.test_run_test_case.value === 'string' ? 
              JSON.parse(detail.test_run_test_case.value) : 
              detail.test_run_test_case.value) : {};
        } catch (error) {
          console.warn('テストケース情報の解析エラー:', error);
        }
        
        return {
          id: detail.script_test_run_result.result_id,
          test_result_status_id: detail.script_test_run_result.test_result_status_id,
          value: detail.script_test_run_result.value,
          test_case_id: detail.test_run_test_case.testcase_id,
          test_case: {
            id: detail.test_run_test_case.testcase_id,
            name: testCaseValue.name || ''  // primary_item, secondary_item, tertiary_itemは廃止
          },
          // REST APIレスポンスとの対応を保持
          _detail: detail
        };
      });
      
      console.log(`取得結果: テスト結果${testResultsInternal.length}件`);

      // human_judgment_confirmedがfalseまたは未設定の結果のみをフィルタ
      // human_judgment_confirmed=trueの結果は人間が確認済みなので、ステータスを変更しない
      const unconfirmedResults = testResultsInternal.filter((result: any) => {
        const value = typeof result.value === 'string' ? JSON.parse(result.value) : result.value;
        const isConfirmed = value?.tag_analysis?.human_judgment_confirmed === true;
        if (isConfirmed) {
          console.log(`[DEBUG] 確認済み結果をスキップ: testCaseId=${result.test_case_id}, statusId=${result.test_result_status_id}`);
        }
        return !isConfirmed;
      });

      console.log(`未確認結果: ${unconfirmedResults.length}件`);
      console.log(`[DEBUG] 未確認結果の詳細:`, unconfirmedResults.map((r: any) => ({
        testCaseId: r.test_case_id,
        statusId: r.test_result_status_id
      })));
      console.log(`確認済み結果（スキップ）: ${testResultsInternal.length - unconfirmedResults.length}件`);

      let updated = 0;
      let skipped = 0;
      let errors = 0;
      
      // 詳細情報を収集
      const detailsAdded: Array<any> = [];
      const detailsRemoved: Array<any> = [];
      const detailsErrors: Array<any> = [];
      
      // ステータス名のマッピングを作成
      const statusIdToName: Record<string, string> = {};
      if (statusIds?.testResultStatus) {
        statusIdToName[statusIds.testResultStatus.AI_OK] = '[AI]OK';
        statusIdToName[statusIds.testResultStatus.AI_OK_NEED_REVIEW] = '[AI]OK(要確認)';
        statusIdToName[statusIds.testResultStatus.AI_PROCEDURE_NG] = '[AI]手順NG';
        statusIdToName[statusIds.testResultStatus.AI_PROCEDURE_NG_NEED_REVIEW] = '[AI]手順NG(要確認)';
        statusIdToName[statusIds.testResultStatus.AI_RESULT_NG] = '[AI]結果NG';
        statusIdToName[statusIds.testResultStatus.AI_RESULT_NG_NEED_REVIEW] = '[AI]結果NG(要確認)';
      }

      // 2. タグ分析サービスを使用
      const { TagAnalysisService } = await import('./tagAnalysisService');
      const tagService = new TagAnalysisService();

      // 3. 未確認結果の自動要確認判定処理
      for (const result of unconfirmedResults) {
        try {
          // testCaseはすでにresultに含まれている
          const testCase = result.test_case;
          if (!testCase) {
            console.warn('テストケース情報が見つかりません:', result.test_case_id);
            errors++;
            continue;
          }

          // タグ統計を計算（action_tagsのみを使用）
          // resultのvalueからaction_tagsを抽出
          const resultValue = typeof result.value === 'string' ? JSON.parse(result.value) : result.value;
          const actionTags: string[] = [];

          // デバッグログ: resultValueの構造を確認
          console.log(`[DEBUG] resultValue構造:`, {
            testCaseId: result.test_case_id,
            hasTagAnalysis: !!resultValue?.tag_analysis,
            hasSteps: !!resultValue?.tag_analysis?.steps,
            stepsCount: resultValue?.tag_analysis?.steps?.length || 0,
            tagAnalysisKeys: resultValue?.tag_analysis ? Object.keys(resultValue.tag_analysis) : []
          });

          // tag_analysis.stepsからaction_tagsを収集
          if (resultValue?.tag_analysis?.steps) {
            resultValue.tag_analysis.steps.forEach((step: any, index: number) => {
              const stepActionTags = step.evaluated_tags?.action_tags || [];

              // 最初の3ステップのみデバッグログ
              if (index < 3) {
                console.log(`[DEBUG] Step ${index}:`, {
                  hasEvaluatedTags: !!step.evaluated_tags,
                  evaluatedTagsKeys: step.evaluated_tags ? Object.keys(step.evaluated_tags) : [],
                  actionTagsInStep: stepActionTags
                });
              }

              stepActionTags.forEach((tag: string) => {
                if (tag && !actionTags.includes(tag)) {
                  actionTags.push(tag);
                }
              });
            });
          }

          console.log(`[DEBUG] 収集したactionTags:`, {
            testCaseId: result.test_case_id,
            actionTagsCount: actionTags.length,
            actionTags: actionTags.slice(0, 10)
          });

          // TagAnalysisService用のデータ形式に変換
          const testResultsForAnalysis = testResultsInternal.map(r => ({
            id: parseInt(r.id, 10) || 0,  // stringをnumberに変換
            status: r.test_result_status_id,  // statusフィールドを追加
            value: r.value,
            test_case_id: parseInt(r.test_case_id, 10) || 0,
            test_case: {
              id: parseInt(r.test_case.id, 10) || 0,
              name: r.test_case.name  // primary_item, secondary_item, tertiary_itemは廃止
            }
          }));

          const tagStats = tagService.calculateTagNgRates(testResultsForAnalysis, actionTags);

          // デバッグログ: タグ統計情報
          console.log(`タグ統計計算完了:`, {
            testCaseId: result.test_case_id,
            actionTagsCount: actionTags.length,
            actionTags: actionTags.slice(0, 5),  // 最初の5つのみ表示
            tagStatsKeys: Object.keys(tagStats).slice(0, 5),
            sampleTagStats: Object.entries(tagStats).slice(0, 2).map(([k, v]) => ({ tag: k, ...v }))
          });

          // 要確認判定（action_tagsのみを使用）
          const { required, reason } = tagService.checkReviewRequired(actionTags, tagStats);

          // デバッグログ: 判定結果
          console.log(`要確認判定結果:`, {
            testCaseId: result.test_case_id,
            required,
            reason: reason?.substring(0, 100)
          });

          // 現在のステータスIDから新しいステータスIDを決定
          const currentStatusId = result.test_result_status_id;
          let newStatusId = currentStatusId;

          // デバッグログ: ステータス変更判定前
          console.log(`[DEBUG] ステータス変更判定:`, {
            testCaseId: result.test_case_id,
            currentStatusId,
            required,
            hasStatusIds: !!statusIds?.testResultStatus,
            currentIsAI_OK: currentStatusId === statusIds?.testResultStatus?.AI_OK,
            currentIsAI_OK_NEED_REVIEW: currentStatusId === statusIds?.testResultStatus?.AI_OK_NEED_REVIEW
          });

          if (required && statusIds?.testResultStatus) {
            // 要確認が必要な場合、要確認ステータスに変更
            if (currentStatusId === statusIds.testResultStatus.AI_OK) {
              newStatusId = statusIds.testResultStatus.AI_OK_NEED_REVIEW || currentStatusId;
              console.log(`[DEBUG] AI_OK -> AI_OK_NEED_REVIEW: ${newStatusId}`);
            } else if (currentStatusId === statusIds.testResultStatus.AI_PROCEDURE_NG) {
              newStatusId = statusIds.testResultStatus.AI_PROCEDURE_NG_NEED_REVIEW || currentStatusId;
              console.log(`[DEBUG] AI_PROCEDURE_NG -> AI_PROCEDURE_NG_NEED_REVIEW: ${newStatusId}`);
            } else if (currentStatusId === statusIds.testResultStatus.AI_RESULT_NG) {
              newStatusId = statusIds.testResultStatus.AI_RESULT_NG_NEED_REVIEW || currentStatusId;
              console.log(`[DEBUG] AI_RESULT_NG -> AI_RESULT_NG_NEED_REVIEW: ${newStatusId}`);
            } else {
              console.log(`[DEBUG] required=true だが、現在のステータスが該当しない: ${currentStatusId}`);
            }
            // すでに要確認ステータスの場合は変更なし
          } else if (!required && statusIds?.testResultStatus) {
            // 要確認が不要になった場合、通常のステータスに戻す
            if (currentStatusId === statusIds.testResultStatus.AI_OK_NEED_REVIEW) {
              newStatusId = statusIds.testResultStatus.AI_OK || currentStatusId;
              console.log(`[DEBUG] AI_OK_NEED_REVIEW -> AI_OK: ${newStatusId}`);
            } else if (currentStatusId === statusIds.testResultStatus.AI_PROCEDURE_NG_NEED_REVIEW) {
              newStatusId = statusIds.testResultStatus.AI_PROCEDURE_NG || currentStatusId;
              console.log(`[DEBUG] AI_PROCEDURE_NG_NEED_REVIEW -> AI_PROCEDURE_NG: ${newStatusId}`);
            } else if (currentStatusId === statusIds.testResultStatus.AI_RESULT_NG_NEED_REVIEW) {
              newStatusId = statusIds.testResultStatus.AI_RESULT_NG || currentStatusId;
              console.log(`[DEBUG] AI_RESULT_NG_NEED_REVIEW -> AI_RESULT_NG: ${newStatusId}`);
            } else {
              console.log(`[DEBUG] required=false だが、現在のステータスが該当しない: ${currentStatusId}`);
            }
            // すでに通常ステータスの場合は変更なし
          }

          console.log(`[DEBUG] 最終ステータス決定:`, {
            testCaseId: result.test_case_id,
            currentStatusId,
            newStatusId,
            statusChanged: newStatusId !== currentStatusId
          });

          // valueを取得してreview_reasonを更新
          const value = typeof result.value === 'string' ? JSON.parse(result.value) : result.value || {};
          const originalReviewReason = value.review_reason || '';

          // コントローラーと同じマーカー形式を使用
          const penaltyMarkerPrefix = '【AI判定正誤実績によるスコア調整';
          const penaltyMarkerRegex = /【AI判定正誤実績によるスコア調整[^】]*】[^【]*/;

          // ペナルティ情報を常に計算（ステータス変更の有無に関わらず）
          const penaltyInfo = tagService.getMaxPenaltyTagInfo(actionTags, tagStats);

          // デバッグログ: ペナルティ情報
          console.log(`ペナルティ情報:`, {
            testCaseId: result.test_case_id,
            penaltyInfo: penaltyInfo ? {
              tag: penaltyInfo.tag,
              penalty: penaltyInfo.penalty,
              ngRate: penaltyInfo.ngRate,
              total: penaltyInfo.total,
              confidence: penaltyInfo.confidence
            } : null
          });

          // ペナルティがある場合はreview_reasonに追記
          if (penaltyInfo && penaltyInfo.penalty > 0) {
            const virtualScore = Math.round(100 - penaltyInfo.penalty);
            const penaltyComment = `【AI判定正誤実績によるスコア調整: -${Math.round(penaltyInfo.penalty)}点】タグ「${penaltyInfo.tag}」(判定${penaltyInfo.total}回, NG率${(penaltyInfo.ngRate * 100).toFixed(0)}%, 信頼度${(penaltyInfo.confidence * 100).toFixed(0)}%, 不確実性ペナルティ${(penaltyInfo.uncertaintyPenalty * 100).toFixed(0)}%)`;

            if (value.review_reason) {
              // 既存のreview_reasonがある場合
              if (value.review_reason.includes(penaltyMarkerPrefix)) {
                // マーカーが既に存在する場合は、その部分を新しい内容で置換（重複防止）
                value.review_reason = value.review_reason.replace(
                  penaltyMarkerRegex,
                  penaltyComment
                );
              } else {
                // マーカーがない場合は追記
                value.review_reason = `${value.review_reason} ${penaltyComment}`;
              }
            } else {
              // 新規設定
              value.review_reason = penaltyComment;
            }
          }

          // review_reasonが変更されたかチェック
          const reviewReasonChanged = value.review_reason !== originalReviewReason;

          // ステータス変更またはreview_reason変更がある場合のみ更新
          const statusChanged = newStatusId !== currentStatusId;

          if (statusChanged || reviewReasonChanged) {
            console.log(`更新実行:`, {
              testCaseId: result.test_case_id,
              statusChanged,
              reviewReasonChanged,
              currentStatusId,
              newStatusId,
              required,
              reason: reason?.substring(0, 50)
            });

            // REST APIを使用してステータスIDとvalueを更新
            try {
              // 内部データから保存されているdetailを取得
              const detail = (result as any)._detail;
              
              if (!detail) {
                console.error(`テスト結果の詳細が見つかりません: ${result.id}`);
                errors++;
                continue;
              }
              
              await this.updateTestResultREST(
                workspaceId,
                projectId,
                detail.test_run.test_run_id,
                result.id,
                detail.script_test_run_result.script_test_run_matrix_id,
                newStatusId,
                JSON.stringify(value)
              );
              
              updated++;
              const action = required ? '要確認ステータス付与' : '要確認ステータス解除';
              console.log(`${action}: ${result.id}: ${currentStatusId} -> ${newStatusId}`, {
                reason: reason || 'NG率が改善'
              });
              
              // 詳細情報を収集
              const testCaseName = detail?.test_run_test_case?.name || `テストケース ${result.test_case_id}`;
              const oldStatusName = statusIdToName[currentStatusId] || currentStatusId;
              const newStatusName = statusIdToName[newStatusId] || newStatusId;
              const updateReason = reason || 'NG率が改善';
              
              if (required) {
                console.log('要確認追加:', { testCaseName, oldStatusName, newStatusName, updateReason });
                detailsAdded.push({
                  testCaseName,
                  oldStatus: oldStatusName,
                  newStatus: newStatusName,
                  reason: updateReason
                });
              } else {
                console.log('要確認削除:', { testCaseName, oldStatusName, newStatusName, updateReason });
                detailsRemoved.push({
                  testCaseName,
                  oldStatus: oldStatusName,
                  newStatus: newStatusName,
                  reason: updateReason
                });
              }
            } catch (updateError) {
              console.error(`テスト結果 ${result.id} の更新に失敗:`, updateError);
              errors++;
              
              // エラー詳細を収集
              const errorDetail = (result as any)._detail;
              const testCaseName = errorDetail?.test_run_test_case?.name || `テストケース ${result.test_case_id}`;
              detailsErrors.push({
                testCaseName,
                error: updateError instanceof Error ? updateError.message : String(updateError)
              });
            }
          } else {
            skipped++;
          }
        } catch (error) {
          console.error(`Failed to process test result ${result.id}:`, error);
          errors++;
          
          // エラー詳細を収集
          const testCaseName = result._detail?.test_run_test_case?.name || `テストケース ${result.test_case_id}`;
          detailsErrors.push({
            testCaseName,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }

      // デバッグログ追加
      console.log('=== 更新詳細情報 ===');
      console.log('detailsAdded:', detailsAdded);
      console.log('detailsRemoved:', detailsRemoved);
      console.log('detailsErrors:', detailsErrors);
      console.log('==================');
      
      const result = {
        success: true,
        total: unconfirmedResults.length,
        updated,
        skipped,
        errors,
        message: `ステータス更新完了: ${updated}件更新、${skipped}件スキップ、${errors}件エラー`,
        details: {
          added: detailsAdded,
          removed: detailsRemoved,
          errors: detailsErrors
        }
      };
      
      console.log('最終的な返却値:', result);
      return result;

    } catch (error) {
      console.error("要確認ステータス更新失敗:", error);
      return {
        success: false,
        total: 0,
        updated: 0,
        skipped: 0,
        errors: 0,
        message: "ステータス更新に失敗しました"
      };
    }
  }

  // 課題ステータス更新メソッド
  async updateIssueStatus(
    workspaceId: string,
    projectId: string,
    issueId: string,
    issueStatusId: string,
    issueData?: any
  ): Promise<any> {
    console.log('課題ステータス更新:', {
      workspaceId,
      projectId,
      issueId,
      issueStatusId
    });

    // 更新対象の課題データを構築
    const updateIssue: any = {
      issue_id: issueId,
      issue_status_id: issueStatusId
    };

    // 既存の課題データがあれば、必要なフィールドを保持
    if (issueData) {
      updateIssue.name = issueData.name;
      
      // valueフィールドの中のissue_statusも更新
      let valueData: any = {};
      if (issueData.value) {
        try {
          valueData = typeof issueData.value === 'string' 
            ? JSON.parse(issueData.value)
            : issueData.value;
        } catch (error) {
          console.error('value フィールドのパースに失敗:', error);
          valueData = {};
        }
      }
      
      // issue_status を新しいステータスIDで更新
      valueData.issue_status = issueStatusId;
      updateIssue.value = JSON.stringify(valueData);
      
      updateIssue.meta_id = issueData.meta_id;
      updateIssue.meta_id_sequence = issueData.meta_id_sequence;
      updateIssue.display_order = issueData.display_order;
      updateIssue.build_version_ids = issueData.build_version_ids || [];
      updateIssue.execution_environment_ids = issueData.execution_environment_ids || [];
      updateIssue.managed_file_ids = issueData.managed_file_ids || [];
      updateIssue.urls = issueData.attached_url_list?.map((url: any) => ({
        id: url.id,
        name: url.name,
        url: url.url
      })) || [];
    }

    const requestData = {
      workspace_id: workspaceId,
      project_id: projectId,
      update_issues: [updateIssue]
    };

    console.log('課題更新リクエストデータ:', {
      workspace_id: workspaceId,
      project_id: projectId,
      update_issue: updateIssue,
      issue_status_id: updateIssue.issue_status_id,
      value_contains_status: updateIssue.value?.includes(issueStatusId)
    });

    try {
      const response = await this.put('/issues', requestData);
      console.log('課題ステータス更新成功:', response);
      return response;
    } catch (error) {
      console.error('課題ステータス更新失敗:', error);
      throw error;
    }
  }
}

// データ型定義（Pythonのapp.pyから抽出）
export interface Workspace {
  id: string;  // APIのレスポンスではidで返される
  workspace_id: string;
  name: string;
  slug: string;
}

export interface Project {
  id: string;  // APIのレスポンスではidで返される
  project_id: string;
  name: string;
  slug: string;
  // SSTF /projects エンドポイントは ai_settings 等を含む JSON 文字列を返す。
  // リグレッション MS-SSIM 閾値（ai_settings.result_analyzer_settings.ms_ssim_*_threshold）の
  // 抽出に使用。未提供 / 取得失敗時は undefined（呼び出し側でデフォルトに委譲）。
  value?: string;
}

export interface TestRun {
  test_run_id: string;
  name: string;
  workspace_id: string;
  project_id: string;
}

export interface FindTestRunsRequest {
  page: number;
  size: number;
  workspace_id: string;
  project_id: string;
}

export interface TestRunWithSummary {
  test_run: {
    workspace_id: string;
    project_id: string;
    test_run_id: string;
    meta_id: string;
    meta_id_sequence: number;
    name: string;
    test_run_status_id: number;
    value: string;
    test_run_matrix: {
      testsuite_id: string;
      execution_environment_ids: string[] | null;
    };
    created_at: string;
    created_by: string;
    modified_at: string;
    modified_by?: string;
  };
  test_run_summary: {
    total_testcases: number;
    total_test_run_matrix: number;
    total_execution_environments: number;
    script_test_run_issue_summaries: Array<{
      issue_status_id: string;
      issue_status_count: number;
    }>;
    script_test_run_result_summaries: Array<{
      test_result_status_id: string;
      test_result_status_count: number;
    }>;
    total_estimated_time_in_seconds: number;
    estimated_time_available_matrix: number;
  };
}

export interface FindTestRunsResponse {
  test_run_with_summaries: TestRunWithSummary[];
  template: string;
  pagination: {
    cur_page: number;
    page_size: number;
    total_page: number;
    has_next: boolean;
    has_prev: boolean;
    total_hits: number;
  };
}

export interface Template {
  template: string; // JSON文字列
}

export interface FindTestResultsRequest {
  page: number;
  size: number;
  workspace_id: string;
  project_id: string;
  query?: {
    or?: Array<{
      key: string;
      type: string;
      value: string;
    }>;
    and?: Array<{
      key: string;
      type: string;
      value: string;
    }>;
  };
}

export interface FindTestResultsResponse {
  script_test_result_details: ScriptTestResultDetail[];
  pagination: {
    has_next: boolean;
    total: number;
  };
}

export interface ScriptTestResultDetail {
  test_run: {
    test_run_id: string;
    meta_id: string;
    name: string;
    workspace_id: string;
    project_id: string;
  };
  test_run_test_suite: {
    testsuite_id: string;
    meta_id: string;
    name: string;
  };
  test_run_test_case: {
    testcase_id: string;
    meta_id: string;
    name: string;
    value: string; // JSON文字列
    display_order: number;
  };
  test_run_execution_environment: {
    execution_environment_id: string;
    name: string;
  };
  script_test_run_result: {
    result_id: string;
    script_test_run_matrix_id: string;
    test_result_status_id: string;
    modified_at: string;
    comment: string;
    value: string; // JSON文字列
    managed_file_list?: ManagedFile[];
  };
  issues?: Issue[];
  script_test_results_issues?: ScriptTestResultsIssue[];
  build_version?: BuildVersion;
}

export interface ManagedFile {
  managed_file_id: string;
  file_name: string;
}

// 課題（Issue）の型定義
export interface Issue {
  issue_id: string;
  meta_id: string;
  meta_id_sequence: number;
  name: string;
  issue_status_id: string;
  value: string;
  display_order: number;
  workspace_id: string;
  project_id: string;
  build_version_ids?: string[];
  execution_environment_ids?: string[];
  attached_url_list?: AttachedUrl[];
  managed_file_list?: ManagedFileWithTimestamp[];
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by?: string;
}

// テスト結果と課題の関連付け
export interface ScriptTestResultsIssue {
  script_test_result_id: string;
  issue_id: string;
  workspace_id: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by?: string;
}

// ビルドバージョン情報
export interface BuildVersion {
  build_version_id: string;
  name: string;
  display_order: number;
  enabled: boolean;
  workspace_id: string;
  project_id: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by?: string;
}

// 添付URL
export interface AttachedUrl {
  id: string;
  name: string;
  url: string;
  workspace_id: string;
  project_id: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by?: string;
}

// タイムスタンプ付きManagedFile
export interface ManagedFileWithTimestamp {
  managed_file_id: string;
  workspace_id: string;
  project_id: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by?: string;
}

export interface FileMetadata {
  file_id: string;
  file_name: string;
  display_name: string;
  status: string;
  mime_type: string;
  tags: string | null;
  category: string;
  file_size: number;
  desc: string;
  owner_id: string;
  created_at: string;
  created_by: string;
  modified_at: string;
  modified_by: string;
}

export interface FilesMetadataResponse {
  project_id: string;
  files_info: FileMetadata[];
}

// 操作ログのデータ型定義
export interface OperationLog {
  trace_id: string;
  exec_mode: string;
  steps: OperationStep[];
  videos: OperationVideo[];
}

export interface OperationStep {
  result: boolean;
  action: string;
  test_target_id: string;
  params: Record<string, any>;
  start_time: number;
  end_time: number;
  start_time_primary?: number;
  end_time_primary?: number;
  analyze_exec: boolean;
  item: {
    name: string;
    description: string;
  };
  evidence: string | null;
  operation_type: string;
}

export interface OperationVideo {
  file: string;
  start_time: number;
}

// テスト結果更新用の型定義
export interface UpdateTestResultsInput {
  workspaceId: string;
  projectId: string;
  testRunId: string;
  testResultInputs: TestResultInput[];
}

export interface TestResultInput {
  actualTimeInSeconds?: number;
  resultId: string;
  scriptTestRunMatrixId: string;
  testResultStatusId: string;
  value?: string;
}

// GraphQLレスポンス用の型定義（シンプル版）
export interface UpdateTestResultsResponse {
  scriptTestRunResult: {
    id: string;
    testResultStatusId: string;
    metaInfo: {
      modifiedAt: string;
      modifiedBy: string;
    };
  };
}

// UTC文字列をJSTに変換（Pythonから移植）
export function convertToJST(utcStr: string | number): string {
  let date: Date;

  if (typeof utcStr === "number") {
    // 数値の場合はそのままタイムスタンプとして扱う
    date = new Date(utcStr);
  } else if (typeof utcStr === "string") {
    // 文字列の場合
    if (/^\d+$/.test(utcStr)) {
      // 数値文字列の場合はタイムスタンプとして扱う
      date = new Date(parseInt(utcStr, 10));
    } else {
      // ISO文字列の場合
      date = new Date(utcStr.replace("Z", "+00:00"));
    }
  } else {
    return "Invalid Date";
  }

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  return date.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// テンプレートからステータス表示名を取得（Pythonから移植）
export function getStatusDisplayName(
  template: Template,
  statusId: string
): string {
  try {
    const templateData = JSON.parse(template.template);
    const statusField = templateData.fields.find(
      (field: any) => field.id === "test_result_status"
    );

    if (statusField) {
      const option = statusField.data.options.find(
        (opt: any) => opt.value === statusId
      );
      if (option) {
        return option.display_name.ja;
      }
    }
    return "未実施";
  } catch (error) {
    console.error("ステータス表示名の取得に失敗:", error);
    return "未実施";
  }
}

// テンプレートからプロジェクト固有のステータスIDを取得
export interface ProjectStatusIds {
  testResultStatus: {
    UNTESTED: string;
    OK: string;
    NG_TO_OK: string;
    NG: string;
    BLOCKING: string;
    PENDING: string;
    TEST_CASE_MODIFICATION: string;
    NA: string;
    NT: string;
    AI_OK: string;
    AI_RESULT_NG: string;
    AI_PROCEDURE_NG: string;
    AI_OK_NEED_REVIEW: string;
    AI_RESULT_NG_NEED_REVIEW: string;
    AI_PROCEDURE_NG_NEED_REVIEW: string;
  };
  aiStatus: {
    NOT_REQUESTED: string;
    TEST_RUN: string;
    ANALYSIS_OF_RESULTS: string;
    ANALYSIS_OF_DEFECTS: string;
    REQUESTED: string;
    QUEUED: string;
    IN_PROGRESS: string;
    COMPLETED: string;
    TERMINATED: string;
    AWAITING_RESTART: string;
    ERROR: string;
  };
}

export function extractProjectStatusIds(template: Template): ProjectStatusIds {
  console.log("=== プロジェクトステータスID抽出開始 ===");

  try {
    const templateData = JSON.parse(template.template);
    console.log("テンプレートデータ解析成功");

    // test_result_statusフィールドからステータスIDを抽出
    const statusField = templateData.fields.find(
      (field: any) => field.id === "test_result_status"
    );
    console.log("test_result_statusフィールド:", statusField);

    // test_result_ai_statusフィールドからAIステータスIDを抽出
    const aiStatusField = templateData.attributes?.find(
      (attr: any) => attr.id === "test_result_ai_status"
    );
    console.log("test_result_ai_statusフィールド:", aiStatusField);

    const testResultStatus: any = {};
    const aiStatus: any = {};

    // テスト結果ステータスIDを抽出
    if (statusField?.data?.options) {
      statusField.data.options.forEach((option: any, index: number) => {
        const displayName =
          option.display_name?.en || option.display_name?.ja || "";
        const globalName = option.global_master_name;

        // display_nameやglobal_master_nameに基づいてマッピング
        if (displayName === "Untested" || globalName === "untested") {
          testResultStatus.UNTESTED = option.value;
          console.log("✅ UNTESTED マッピング:", option.value);
        } else if (displayName === "OK") {
          testResultStatus.OK = option.value;
          console.log("✅ OK マッピング:", option.value);
        } else if (displayName === "NG→OK") {
          testResultStatus.NG_TO_OK = option.value;
          console.log("✅ NG_TO_OK マッピング:", option.value);
        } else if (displayName === "NG") {
          testResultStatus.NG = option.value;
          console.log("✅ NG マッピング:", option.value);
        } else if (displayName === "Blocking" || displayName === "BK") {
          testResultStatus.BLOCKING = option.value;
          console.log("✅ BLOCKING マッピング:", option.value);
        } else if (displayName === "Pending" || displayName === "保留") {
          testResultStatus.PENDING = option.value;
          console.log("✅ PENDING マッピング:", option.value);
        } else if (
          displayName === "TestCaseModification" ||
          displayName === "要テストケース修正"
        ) {
          testResultStatus.TEST_CASE_MODIFICATION = option.value;
          console.log("✅ TEST_CASE_MODIFICATION マッピング:", option.value);
        } else if (displayName === "N/A") {
          testResultStatus.NA = option.value;
          console.log("✅ NA マッピング:", option.value);
        } else if (displayName === "N/T") {
          testResultStatus.NT = option.value;
          console.log("✅ NT マッピング:", option.value);
        } else if (displayName === "[AI]OK") {
          testResultStatus.AI_OK = option.value;
          console.log("✅ AI_OK マッピング:", option.value);
        } else if (
          displayName === "[AI]Result NG" ||
          displayName === "[AI]結果NG"
        ) {
          testResultStatus.AI_RESULT_NG = option.value;
          console.log("✅ AI_RESULT_NG マッピング:", option.value);
        } else if (
          displayName === "[AI]Procedure NG" ||
          displayName === "[AI]手順NG"
        ) {
          testResultStatus.AI_PROCEDURE_NG = option.value;
          console.log("✅ AI_PROCEDURE_NG マッピング:", option.value);
        } else if (
          displayName === "[AI]OK(Need Review)" ||
          displayName === "[AI]OK(要確認)"
        ) {
          testResultStatus.AI_OK_NEED_REVIEW = option.value;
          console.log("✅ AI_OK_NEED_REVIEW マッピング:", option.value);
        } else if (
          displayName === "[AI]Result NG(Need Review)" ||
          displayName === "[AI]結果NG(要確認)"
        ) {
          testResultStatus.AI_RESULT_NG_NEED_REVIEW = option.value;
          console.log("✅ AI_RESULT_NG_NEED_REVIEW マッピング:", option.value);
        } else if (
          displayName === "[AI]Procedure NG(Need Review)" ||
          displayName === "[AI]手順NG(要確認)"
        ) {
          testResultStatus.AI_PROCEDURE_NG_NEED_REVIEW = option.value;
          console.log(
            "✅ AI_PROCEDURE_NG_NEED_REVIEW マッピング:",
            option.value
          );
        } else {
          console.log("⚠️ マッピングされていないオプション:", {
            displayName,
            globalName,
            value: option.value,
          });
        }
      });
    } else {
      console.log("⚠️ ステータスオプションが見つかりません");
    }

    // AIステータスIDを抽出
    if (aiStatusField?.data?.options) {
      console.log(
        "AIステータスオプション数:",
        aiStatusField.data.options.length
      );
      aiStatusField.data.options.forEach((option: any) => {
        const globalName = option.global_master_name;

        if (globalName === "not_requested") {
          aiStatus.NOT_REQUESTED = option.value;
        } else if (globalName === "test_run") {
          aiStatus.TEST_RUN = option.value;
        } else if (globalName === "analysis_of_results") {
          aiStatus.ANALYSIS_OF_RESULTS = option.value;
        } else if (globalName === "analysis_of_defects") {
          aiStatus.ANALYSIS_OF_DEFECTS = option.value;
        } else if (globalName === "requested") {
          aiStatus.REQUESTED = option.value;
        } else if (globalName === "queued") {
          aiStatus.QUEUED = option.value;
        } else if (globalName === "in_progress") {
          aiStatus.IN_PROGRESS = option.value;
        } else if (globalName === "completed") {
          aiStatus.COMPLETED = option.value;
        } else if (globalName === "terminated") {
          aiStatus.TERMINATED = option.value;
        } else if (globalName === "awaiting_restart") {
          aiStatus.AWAITING_RESTART = option.value;
        } else if (globalName === "error") {
          aiStatus.ERROR = option.value;
        }
      });
    }

    const result = {
      testResultStatus,
      aiStatus,
    };

    console.log("抽出されたステータスIDs:", result);
    console.log("=== プロジェクトステータスID抽出完了 ===");

    return result;
  } catch (error) {
    console.error("❌ プロジェクトステータスIDの抽出に失敗:", error);
    throw new Error(
      `プロジェクトテンプレートからステータスIDを抽出できませんでした。テンプレートの構造を確認してください: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}

// テストターゲットの型定義
export interface TestTarget {
  name: string;
  displayOrder: number;
  workspaceId: string;
  projectId: string;
  testTargetId: string;
  type: {
    typeId: string;
    name: string;
  };
  description: string;
  parentTestTargetId: string | null;
  params: string; // JSON文字列
  metaInfo: {
    createdAt: string;
    createdBy: string;
    modifiedAt: string;
    modifiedBy: string;
  };
}

// テストステップ用のステータスIDを抽出
export interface TestStepStatusIds {
  OK: string;
  NG: string;
  SUCCESS_IDS: string[]; // 成功と判定すべきすべてのステータスID
  WARNING_IDS: string[]; // 警告（要確認）と判定すべきすべてのステータスID
}

export function extractTestStepStatusIds(
  template: Template
): TestStepStatusIds {
  console.log("=== テストステップステータスID抽出開始 ===");
  console.log("テンプレート全体:", template);

  try {
    const templateData = JSON.parse(template.template);
    console.log("テストステップテンプレートデータ解析成功");
    console.log("テンプレートデータ全体:", templateData);
    console.log("fieldsの数:", templateData.fields?.length);

    // 全てのフィールドを出力して構造を確認
    if (templateData.fields) {
      console.log("=== 全フィールド一覧 ===");
      templateData.fields.forEach((field: any, index: number) => {
        console.log(`フィールド ${index + 1}:`, {
          id: field.id,
          type: field.type,
          label: field.label,
          hasData: !!field.data,
          hasOptions: !!field.data?.options,
          optionsCount: field.data?.options?.length || 0,
        });
      });
    }

    // test_result_statusフィールドを探す（テストステップも同じテンプレートを使用）
    const statusField = templateData.fields.find(
      (field: any) => field.id === "test_result_status"
    );
    console.log("test_result_statusフィールド:", statusField);

    // 見つからない場合、類似のフィールドを探す
    if (!statusField) {
      console.log(
        "test_result_statusが見つかりません。類似フィールドを検索中..."
      );
      const statusRelatedFields = templateData.fields.filter(
        (field: any) =>
          field.id.includes("status") || field.id.includes("result")
      );
      console.log("ステータス関連フィールド:", statusRelatedFields);
    }

    const testStepStatus: any = {
      OK: "",
      NG: "",
      SUCCESS_IDS: [], // 成功と判定すべきすべてのステータスID
      WARNING_IDS: [], // 警告（要確認）と判定すべきすべてのステータスID
    };

    // テストステップ結果ステータスIDを抽出
    if (statusField?.data?.options) {
      console.log(
        "テストステップステータスオプション数:",
        statusField.data.options.length
      );
      console.log("=== 全オプション詳細 ===");

      const successIds: string[] = [];
      const warningIds: string[] = [];

      statusField.data.options.forEach((option: any, index: number) => {
        const displayNameEn = option.display_name?.en || "";
        const displayNameJa = option.display_name?.ja || "";
        const testResultType = option.test_result_type?.value;

        console.log(`オプション ${index + 1}:`, {
          value: option.value,
          displayNameEn,
          displayNameJa,
          testResultType,
          fullOption: option,
        });

        // test_result_type の値に基づいて成功/失敗を判定
        // "1" = 成功系（OK、NG→OK、[AI]OKなど）
        if (testResultType === "1") {
          successIds.push(option.value);
          console.log(
            "✅ 成功ステータスとして追加:",
            option.value,
            displayNameJa
          );
        }

        // "5" = 警告系（要確認）
        if (testResultType === "5") {
          warningIds.push(option.value);
          console.log(
            "⚠️ 警告ステータスとして追加:",
            option.value,
            displayNameJa
          );
        }

        // 個別のマッピング（後方互換性のため）
        if (displayNameEn === "OK" && displayNameJa === "OK") {
          testStepStatus.OK = option.value;
          console.log("✅ テストステップ OK マッピング:", option.value);
        } else if (displayNameEn === "NG" && displayNameJa === "NG") {
          testStepStatus.NG = option.value;
          console.log("✅ テストステップ NG マッピング:", option.value);
        }
      });

      // パッチ対応: 特定のAIステータスIDを追加（後で削除予定）
      // 定数 STEP_STATUS_IDS を使用
      const patchSuccessIds = [
        STEP_STATUS_IDS.AI_OK,
      ];
      const patchWarningIds = [
        STEP_STATUS_IDS.AI_OK_NEED_REVIEW,
        STEP_STATUS_IDS.AI_RESULT_NG_NEED_REVIEW,
        STEP_STATUS_IDS.AI_PROCEDURE_NG_NEED_REVIEW,
      ];

      // パッチIDを追加（重複を避ける）
      patchSuccessIds.forEach(id => {
        if (!successIds.includes(id)) {
          successIds.push(id);
          console.log("🔧 パッチ: 成功ステータスIDを追加:", id);
        }
      });
      patchWarningIds.forEach(id => {
        if (!warningIds.includes(id)) {
          warningIds.push(id);
          console.log("🔧 パッチ: 警告ステータスIDを追加:", id);
        }
      });

      testStepStatus.SUCCESS_IDS = successIds;
      testStepStatus.WARNING_IDS = warningIds;
      console.log("✅ 成功ステータスIDs一覧:", successIds);
      console.log("✅ 警告ステータスIDs一覧:", warningIds);
    } else {
      console.log("⚠️ テストステップステータスオプションが見つかりません");
      console.log("statusField:", statusField);
      console.log("statusField.data:", statusField?.data);
    }

    const result = testStepStatus;

    console.log("抽出されたテストステップステータスIDs:", result);
    console.log("=== テストステップステータスID抽出完了 ===");

    return result;
  } catch (error) {
    console.error("❌ テストステップステータスIDの抽出に失敗:", error);
    throw new Error(
      `テストステップテンプレートからステータスIDを抽出できませんでした。テンプレートの構造を確認してください: ${
        error instanceof Error ? error.message : error
      }`
    );
  }
}
